// To update the ports SVG:
// 1) Convert the HTML formatted SVG file to a React friendly Javascript format.
// 2) From the command line: npx @svgr/cli Ports_V3.svg > PortsTemp.jsx
// 3) Cut everything inside the <svg></svg> from the newly formatted jsx file
//    and paste (replace) in PortControl.tsx. Leave the component wrapper intact.
import React from 'react';
import { useColorScheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import '../Controller/Controller.css';

export interface PortControlProps {
  /**
   * Status for ports 1-8. Index 0 = port 1, etc.
   * true = port has a device connected (highlighted), false = empty port.
   * If not provided, all ports are shown as active.
   */
  portStatus?: boolean[];
  /** When set, port slots become interactive buttons that invoke this callback. */
  onPortClick?: (portNumber: number) => void;
  /**
   * Index 0 = port 1. Renders “in use elsewhere” styling (red / usedFill) when true.
   * Only used when `onPortClick` is set.
   */
  portUsedByOther?: boolean[];
  /**
   * When `onPortClick` is set and a port is used by another device, set `aria-haspopup="dialog"`
   * on that slot (configure-panel conflict confirmation).
   */
  conflictPresentation?: 'dialog' | 'inline';
}

const DEFAULT_PORT_STATUS: boolean[] = Array(8).fill(true);
const EMPTY_PORT_STATUS: boolean[] = Array(8).fill(false);

function portIndexForNumber(portNumber: number): number {
  return Math.max(0, Math.min(portNumber - 1, 7));
}

type PortSlotGroupProps = React.SVGProps<SVGGElement> & {
  'data-active'?: 'true' | 'false';
  'data-port-used-by-other'?: 'true' | 'false';
};

const PortControl: React.FC<PortControlProps> = ({ portStatus, onPortClick, portUsedByOther, conflictPresentation = 'dialog' }) => {
  const { t } = useTranslation();
  const resolvedPortStatus = portStatus ?? (onPortClick ? EMPTY_PORT_STATUS : DEFAULT_PORT_STATUS);
  const { mode, systemMode } = useColorScheme();
  const visualMode = (mode === 'system' ? systemMode || 'light' : mode) as 'light' | 'dark';

  const status = React.useMemo(() => {
    const arr = [...resolvedPortStatus];
    while (arr.length < 8) arr.push(false);
    return arr.slice(0, 8);
  }, [resolvedPortStatus]);

  const usedByOtherStatus = React.useMemo(() => {
    const arr = [...(portUsedByOther ?? [])];
    while (arr.length < 8) arr.push(false);
    return arr.slice(0, 8);
  }, [portUsedByOther]);

  const portSlotProps = React.useCallback(
    (portNumber: number): PortSlotGroupProps => {
      const portIndex = portIndexForNumber(portNumber);
      const isActive = status[portIndex];
      const usedByOther = usedByOtherStatus[portIndex];
      const interactive = onPortClick != null;

      const props: PortSlotGroupProps = {
        className: interactive ? 'port-slot port-slot-interactive' : 'port-slot',
        'data-active': isActive ? 'true' : 'false',
      };

      if (!interactive) {
        return props;
      }

      const showConflictDialog = conflictPresentation === 'dialog' && usedByOther;
      props['data-port-used-by-other'] = usedByOther ? 'true' : 'false';
      props.role = 'button';
      props.tabIndex = 0;
      props['aria-label'] = t('portSelector.port_aria', { port: portNumber, defaultValue: 'Port {{port}}' });
      props['aria-pressed'] = isActive;
      if (showConflictDialog) {
        props['aria-haspopup'] = 'dialog';
      }
      props.onClick = () => onPortClick(portNumber);
      props.onKeyDown = (event: React.KeyboardEvent<SVGGElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPortClick(portNumber);
        }
      };

      return props;
    },
    [conflictPresentation, onPortClick, status, t, usedByOtherStatus]
  );

  return (
    <div className={`controller-container port-control${onPortClick ? ' port-control-interactive' : ''} ${visualMode === 'dark' ? 'dark' : 'light'}`}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 418.784 154.398"
        id="Layer_1"
        width={418.784}
        height={154.398}
        data-name="Layer 1"
        preserveAspectRatio="xMidYMid meet"
      >
        <g
          id="g171"
          style={{
            display: 'inline',
            strokeWidth: 1.03173,
          }}
          transform="matrix(.96938 0 0 .9691 -500.036 -922.555)"
        >
          <g
            id="g85"
            style={{
              display: 'inline',
              stroke: '#000101',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="translate(334.32 734.048)"
          >
            <g
              id="g66"
              style={{
                display: 'inline',
                stroke: '#000101',
                strokeWidth: 1.03173,
                strokeOpacity: 1,
              }}
              transform="translate(0 -2)"
            >
              <path
                id="path28"
                d="m278.711 349.909.447.41.541.21.284.03c39.168.03 78.336.05 117.504.05.231 0 .386-.04.54-.1l.516-.3.412-.5.155-.31v-99l-.12-.76-.352-.64-.524-.43-.215-.08-.18-.04-.215-.03h-13.537l-6.528.01h-7.233c-4.277 0-8.546 0-12.815-.01l-48.196-.15c-9.792-.04-19.532-.09-29.23-.15h-.223c-.344.06-.687.22-.928.5l-.343.6-.12.69v99.02l.077.5.275.48z"
                className="cls-2"
                style={{
                  fill: 'none',
                  fillOpacity: 1,
                  stroke: '#000101',
                  strokeWidth: 2.26981,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeDasharray: 'none',
                  strokeOpacity: 1,
                }}
              />
              <path
                id="path30"
                d="M272.361 274.16c.01-8.34.027-16.69.053-25.06v0-.01 0-.02c0-.91.166-1.8.472-2.64.157-.42.35-.82.568-1.2.227-.38.48-.74.768-1.06a6.1 6.1 0 0 1 1.984-1.5 5.6 5.6 0 0 1 2.324-.53v0c39.847.08 79.704.12 119.56.12.804 0 1.59.18 2.333.54.376.18.725.4 1.057.66s.647.56.926.89q.422.495.752 1.08c.218.38.41.8.559 1.22.306.86.454 1.77.463 2.7v99.88c-.019.442-.016.564-.044.87-.043.74-.166 1.46-.428 2.14-.148.4-.34.8-.559 1.18-.218.37-.48.72-.769 1.03-.28.31-.585.59-.926.84-.332.24-.69.46-1.057.63-.725.33-1.529.51-2.324.51h-1.25c-19.299 0-38.99-.07-58.561-.19-19.937-.13-39.865-.33-59.794-.57a5.56 5.56 0 0 1-2.315-.54 5.8 5.8 0 0 1-1.048-.64 6 6 0 0 1-.926-.86c-.28-.32-.542-.67-.76-1.04-.228-.38-.42-.78-.568-1.2a7.7 7.7 0 0 1-.48-2.66v0c-.036-16.52-.053-33.04-.053-49.64l.017-24.95z"
                className="cls-2"
                style={{
                  display: 'inline',
                  fill: 'none',
                  fillOpacity: 1,
                  stroke: '#000101',
                  strokeWidth: 2.26981,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeDasharray: 'none',
                  strokeOpacity: 1,
                }}
              />
            </g>
          </g>
          <g
            id="back_button"
            style={{
              fill: 'none',
              stroke: '#000',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 593.319 787.63)"
          >
            <circle
              id="circle93"
              cx={65.799}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.615022,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path92"
              d="M485.53 345.38c-1.18 0-2.27.3-3.2.8-.17-.42-.28-.8-.31-1.1a.535.535 0 0 0-.61-.4c-.11.02-.21.07-.32.19l-3.84 4.98c-.11.14-.15.27-.14.4v.04q0 0 0 0c0 .08.03.16.07.23.07.12.17.2.29.24.09.03.2.05.33.03l6.22-.92c.16-.03.25-.1.32-.19a.54.54 0 0 0-.05-.73c-.27-.19-.57-.5-.88-.89.62-.34 1.33-.55 2.13-.55 2.48 0 4.45 2.05 4.45 4.64s-1.97 4.62-4.45 4.62c-1.35 0-2.48-.58-3.27-1.47-.3-.26-.6-.43-.88-.43-.62 0-1.11.49-1.11 1.09 0 .24.09.55.3.75 1.15 1.32 2.91 2.2 4.96 2.2 3.76 0 6.73-2.99 6.73-6.77s-2.97-6.77-6.73-6.77z"
              className="cls-4"
              style={{
                fill: '#010101',
                fillOpacity: 1,
                stroke: '#010101',
                strokeWidth: 1.81585,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
              transform="matrix(.26458 0 0 .26458 -62.422 -14.706)"
            />
          </g>
          <g
            id="up_button"
            style={{
              fill: 'none',
              stroke: '#000',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 539.63 763.139)"
          >
            <circle
              id="path1-2-3-3-4"
              cx={65.846}
              cy={-62.571}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.615022,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
              transform="scale(1 -1)"
            />
            <path
              id="path60-3"
              d="m67.246 63.605-1.124-2.791a.3.3 0 0 0-.132-.156l-.016-.008q0 0 0 0a.3.3 0 0 0-.111-.027.26.26 0 0 0-.17.061.3.3 0 0 0-.092.13l-1.128 2.791a.255.255 0 0 0 .315.333c.23-.105.625-.177 1.072-.177s.841.072 1.071.177a.256.256 0 0 0 .315-.333"
              className="cls-4"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.609564,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="down_button"
            style={{
              fill: 'none',
              stroke: '#000',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 539.63 751.139)"
          >
            <circle
              id="path1-2-3-3-4-1-3"
              cx={65.799}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.615022,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path61-6"
              d="m64.473 77.302 1.125 2.792c.032.077.08.127.132.156l.016.008v0a.3.3 0 0 0 .111.026.27.27 0 0 0 .17-.06.3.3 0 0 0 .092-.13l.992-2.458.135-.334a.255.255 0 0 0-.315-.333c-.23.106-.624.177-1.071.177s-.842-.071-1.072-.177a.256.256 0 0 0-.315.333"
              className="cls-4"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.609564,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="right_button"
            style={{
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 533.63 757.139)"
          >
            <circle
              id="path1-2-3-3"
              cx={73.72}
              cy={70.443}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.615022,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path62-7"
              d="m72.71 71.89 2.791-1.125a.3.3 0 0 0 .156-.133l.008-.016q0 0 0 0a.3.3 0 0 0 .027-.11.26.26 0 0 0-.061-.17.3.3 0 0 0-.13-.093l-2.791-1.127a.255.255 0 0 0-.333.315c.105.23.177.625.177 1.072s-.072.841-.177 1.071a.256.256 0 0 0 .333.315"
              className="cls-4"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.609564,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="left_button"
            style={{
              fill: 'none',
              stroke: '#000',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 545.63 757.139)"
          >
            <circle
              id="path1-2-3-3-4-1"
              cx={57.834}
              cy={70.485}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.615022,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path63-6"
              d="m58.962 69.116-2.791 1.125a.3.3 0 0 0-.156.132l-.008.016q0 0 0 0a.3.3 0 0 0-.027.111c0 .066.024.124.061.17a.3.3 0 0 0 .13.092l2.791 1.127a.255.255 0 0 0 .333-.315c-.105-.23-.177-.624-.177-1.071s.072-.842.178-1.072a.256.256 0 0 0-.334-.315"
              className="cls-4"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.609564,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="accept_button"
            style={{
              fill: 'none',
              stroke: '#000',
              strokeWidth: 1.03173,
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 559.992 787.567)"
          >
            <circle
              id="circle96"
              cx={65.752}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.615022,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path91"
              d="M522.69 350.8c-.11-.26-.37-.53-.63-.63a1.04 1.04 0 0 0-1.32.62c-.08.21-.09.43 0 .72l2.56 6.24a1.26 1.26 0 0 0 .6.67c.14.07.29.11.46.11.26 0 .5-.09.68-.25.15-.12.28-.29.37-.52l4.57-11.32c.09-.29.08-.51 0-.72a1.04 1.04 0 0 0-1.32-.62c-.27.1-.53.37-.63.63l-3.65 8.92-1.68-3.84z"
              className="cls-4"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000100',
                strokeWidth: 1.81585,
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="matrix(.26458 0 0 .26458 -73.206 -14.69)"
            />
          </g>
          <path
            id="path21-7"
            d="M841.177 977.099h-14.19c-.466 0-.61.39-.188.624.586.33 3.143 1.79 3.143 1.79 1.752 1.06.459 2.646.459 2.646a13.93 13.93 0 0 0-3.249 8.964c0 7.746 6.28 14.025 14.025 14.025 7.746 0 14.025-6.28 14.025-14.025s-6.28-14.024-14.025-14.024z"
            style={{
              display: 'inline',
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 2.33578,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path22"
            d="M849.825 991.123c0 1.76-.978 3.181-2.18 3.181-1.204 0-2.182-1.429-2.182-3.18 0-1.753.978-3.182 2.181-3.182s2.181 1.43 2.181 3.181z"
            style={{
              display: 'inline',
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 2.33578,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path23-7"
            d="M843.35 991.123c0 2.467-1.368 4.467-3.06 4.467s-3.061-2-3.061-4.467 1.369-4.467 3.06-4.467c1.693 0 3.061 2 3.061 4.467z"
            style={{
              display: 'inline',
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 2.33578,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
        </g>
        <rect
          id="rect171"
          width={380.126}
          height={151.398}
          x={18.853}
          y={-152.898}
          rx={2.373}
          ry={3}
          style={{
            fill: 'none',
            stroke: '#000',
            strokeWidth: 3,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
            strokeOpacity: 1,
          }}
          transform="scale(1 -1)"
        />
        <text
          xmlSpace="preserve"
          id="text171"
          x={-1.09}
          y={38.851}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171"
            x={-1.09}
            y={38.851}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'1'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-2"
          x={-0.617}
          y={70.313}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-4"
            x={-0.617}
            y={70.313}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'2'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-2-9"
          x={0.095}
          y={99.699}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-4-7"
            x={0.095}
            y={99.699}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'3'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-2-9-7"
          x={-0.138}
          y={130.608}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-4-7-5"
            x={-0.138}
            y={130.608}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'4'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-7"
          x={406.299}
          y={38.544}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-1"
            x={406.299}
            y={38.544}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'5'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-2-5"
          x={406.772}
          y={70.741}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-4-5"
            x={406.772}
            y={70.741}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'6'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-2-9-8"
          x={407.484}
          y={101.198}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-4-7-56"
            x={407.484}
            y={101.198}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'7'}
          </tspan>
        </text>
        <text
          xmlSpace="preserve"
          id="text171-2-9-7-7"
          x={407.251}
          y={131.728}
          style={{
            fontWeight: 700,
            fontSize: '21.3333px',
            fontFamily: 'Helvetica',
            fill: '#010101',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 0.1,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
        >
          <tspan
            id="tspan171-4-7-5-9"
            x={407.251}
            y={131.728}
            style={{
              fontSize: '21.3333px',
            }}
          >
            {'8'}
          </tspan>
        </text>
        <rect
          id="rect173"
          width={41.568}
          height={129.28}
          x={30.194}
          y={-142.523}
          rx={3}
          ry={3}
          style={{
            fill: 'none',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 3,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
          transform="scale(1 -1)"
        />
        <rect
          id="rect173-5"
          width={41.568}
          height={129.28}
          x={345.817}
          y={-142.134}
          rx={3}
          ry={3}
          style={{
            fill: 'none',
            fillOpacity: 1,
            stroke: '#000',
            strokeWidth: 3,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 10,
            strokeDasharray: 'none',
          }}
          transform="scale(1 -1)"
        />
        <g
          id="port1"
          data-port={1}
          {...portSlotProps(1)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="rotate(90 25.049 41.203)scale(1.25)"
        >
          <path
            id="path174"
            d="m1.167 22.86 21.47.076.42-13.043-.534-1.106-2.937-.266-.343-2.86-2.86-.916-.42-3.203-1.41-.572-6.14.19-.649 1.297.19 2.593-3.088.153-.382 3.28-1.067.343-2.403-.039Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.08,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 17.76,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 17.12,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 17.12,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 17.12,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 17.12,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port2"
          data-port={2}
          {...portSlotProps(2)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="rotate(90 9.483 56.616)scale(1.25)"
        >
          <path
            id="path174-4"
            d="m1.272 22.753 21.47.076.42-13.042-.534-1.106-2.936-.267-.343-2.86-2.86-.916-.42-3.203-1.411-.572-6.14.19L7.87 2.35l.19 2.593-3.089.153-.38 3.28-1.069.343L1.12 8.68z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.08,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-9"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-7"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-1"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-7"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-4"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port3"
          data-port={3}
          {...portSlotProps(3)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="rotate(90 -5.85 72.24)scale(1.25)"
        >
          <path
            id="path174-4-5"
            d="m1.036 23.153 21.83.077.427-13.26-.543-1.125-2.986-.272-.349-2.908-2.908-.93-.426-3.257-1.435-.582-6.243.194-.659 1.318.194 2.637-3.14.155-.389 3.335-1.085.349-2.443-.04Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.0999998,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-9-1"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-7-9"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-1-4"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-7-5"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-4-9"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port4"
          data-port={4}
          {...portSlotProps(4)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="rotate(90 -21.425 87.716)scale(1.25)"
        >
          <path
            id="path174-4-5-6"
            d="m1.333 22.803 21.172.075.413-12.86-.526-1.091-2.896-.264-.338-2.82-2.82-.903-.414-3.158-1.392-.564-6.054.188-.64 1.278.189 2.557-3.046.15-.376 3.235-1.053.338-2.37-.037Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.125,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-9-1-7"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-7-9-0"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-1-4-1"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-7-5-3"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-4-9-4"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port8"
          data-port={8}
          {...portSlotProps(8)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="rotate(-90 245.166 -106.17)scale(1.25)"
        >
          <path
            id="path174-4-5-6-7-9-5-2"
            d="m.905 23.018 21.863.078.427-13.28-.544-1.127-2.99-.272-.35-2.912-2.912-.932-.427-3.262L14.535.73 8.283.923l-.66 1.32.194 2.64-3.145.156-.389 3.34-1.087.35-2.446-.04Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.24414,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-8"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-2"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-6"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-0"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-3"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port7"
          data-port={7}
          {...portSlotProps(7)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="matrix(0 -1.25 1.25 0 351.488 108.018)"
        >
          <path
            id="path174-4-5-6-7-9-5"
            d="m.878 23.048 21.919.078.428-13.315-.545-1.129-2.998-.272-.35-2.92-2.92-.935-.428-3.27-1.44-.584-6.269.195-.662 1.324.195 2.647-3.153.156-.39 3.348-1.09.35-2.453-.039Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.195312,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-9-4"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-7-96"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-1-5"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-7-1"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-4-3"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port6"
          data-port={6}
          {...portSlotProps(6)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="matrix(0 -1.25 1.25 0 351.198 77.06)"
        >
          <path
            id="path174-4-5-6-7-9"
            d="m.925 22.996 21.918.077.429-13.314-.545-1.129-2.998-.273-.35-2.92-2.92-.934-.429-3.27L14.59.65 8.322.844 7.66 2.167l.195 2.648L4.7 4.97l-.39 3.349-1.09.35L.77 8.63Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.195312,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-9-1-6"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-7-9-9"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-1-4-0"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-7-5-0"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-4-9-3"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
        <g
          id="port5"
          data-port={5}
          {...portSlotProps(5)}
          style={{
            fill: 'none',
            strokeWidth: 0.8,
          }}
          transform="matrix(0 -1.25 1.25 0 351.295 46.01)"
        >
          <path
            id="path174-4-5-6-7"
            d="m.993 22.85 21.668.077.423-13.162-.539-1.116-2.963-.27-.346-2.886-2.887-.924-.423-3.232-1.424-.578-6.196.193L7.65 2.26l.193 2.617-3.118.154-.384 3.31-1.078.346L.839 8.65Z"
            style={{
              fill: '#ff01ff',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.15625,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path1-9-1-7-5"
            fill="#fff"
            d="M22 7.62h-1.67V6c0-1.1-.9-2-2-2H17V2.09C17 .94 16.06 0 14.91 0H9.08C7.93 0 6.99.94 6.99 2.09V4H5.66c-1.1 0-2 .9-2 2v1.62H1.99c-1.1 0-2 .9-2 2V22c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V9.62c0-1.1-.9-2-2-2zM9 2.09S9.04 2 9.09 2h5.83s.09.04.09.09V4h-6V2.09ZM22 22H2V9.62h1.67c1.1 0 2-.9 2-2V6h12.67v1.62c0 1.1.9 2 2 2h1.67V22Z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path2-7-9-0-3"
            fill="#fff"
            d="M7 16H5v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path3-1-4-1-1"
            fill="#fff"
            d="M11 16H9v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path4-7-5-3-1"
            fill="#fff"
            d="M15 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
          <path
            id="path5-4-9-4-2"
            fill="#fff"
            d="M19 16h-2v4h2z"
            style={{
              fill: '#010101',
              fillOpacity: 1,
              stroke: 'none',
              strokeWidth: 0.8,
              strokeOpacity: 1,
            }}
          />
        </g>
      </svg>
    </div>
  );
};

export default PortControl;
